import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  MyCandidateFromAPI,
  CandidateDetail,
  CandidateStatusEnum,
} from "../../types/candidate";
import { apiClient } from "../../lib/api-client";
import { cn } from "../../lib/utils";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import { EmptyState } from "../ui/EmptyState";
import DangerCircleLinear from "~icons/solar/danger-circle-linear";
import { CandidateProfileView } from "./CandidateProfileView";

// Re-export CollapsibleContent so any existing external imports keep working.
export { CollapsibleContent } from "./shared-candidate-components";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CandidateDetailContentProps {
  /** Candidate list-level data (has confidence_score, candidate_id, etc.) */
  candidate: MyCandidateFromAPI;
  /** JD path to enrich candidate with status fields (score, status, source). */
  jdPath?: string;
  /** Callback when a contact action is triggered */
  onContactAction?: (message: string) => void;
  /**
   * Callback when the user selects a new status from the dropdown.
   * Only called when `candidate.id` is present (CandidateStatus doc ID).
   */
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  /** Whether a status update is in progress — shows a loading indicator */
  isStatusUpdating?: boolean;
  /** Optional className for the root container */
  className?: string;
  /** "modal" renders the tab pills in a white strip above a grey body (v3 CandidatesModal). */
  layout?: "page" | "modal";
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function CandidateDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-label="Loading candidate details">
      <Skeleton className="h-10 w-80 rounded-full" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-5 lg:col-span-4">
          <Card padding="lg" className="rounded-4xl">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="size-20 rounded-full" />
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-9 w-36 rounded-full" />
            </div>
            <div className="mt-5 flex flex-col gap-3 border-t border-black/[0.06] pt-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          </Card>
          <Card padding="lg" className="rounded-4xl">
            <Skeleton className="h-3.5 w-32" />
            <div className="mt-4 flex flex-col gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="flex flex-col gap-6 lg:col-span-8">
          <Card padding="lg" className="rounded-4xl">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="mt-4 h-20 w-full" />
          </Card>
          <Card padding="lg" className="rounded-4xl">
            <Skeleton className="h-5 w-40" />
            <div className="mt-4 flex flex-col gap-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-2xl" />
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State
// ---------------------------------------------------------------------------

function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <Card padding="lg" className="rounded-4xl">
      <EmptyState
        icon={DangerCircleLinear}
        title="Couldn't load candidate details"
        description="Check your connection and try again."
        action={
          onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          )
        }
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Component — fetches the detail record, renders the shared profile view
// ---------------------------------------------------------------------------

export function CandidateDetailContent({
  candidate,
  jdPath,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
  className,
  layout = "page",
}: CandidateDetailContentProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["candidates", candidate.candidate_id, jdPath] as const,
    queryFn: ({ signal }) => {
      const params = jdPath ? `?jd_path=${encodeURIComponent(jdPath)}` : "";
      return apiClient.get<{ data: CandidateDetail }>(
        `/candidates/${candidate.candidate_id}${params}`,
        { signal }
      );
    },
    select: (res) => res.data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleRetry = useCallback(() => refetch(), [refetch]);

  if (isLoading || error || !data) {
    return (
      <div className={cn(layout === "modal" && "bg-surface-2 p-6", className)}>
        {isLoading ? <CandidateDetailSkeleton /> : <ErrorState onRetry={handleRetry} />}
      </div>
    );
  }

  return (
    <CandidateProfileView
      variant="project"
      layout={layout}
      className={className}
      data={data}
      candidateId={candidate.candidate_id}
      candidate={candidate}
      onContactAction={onContactAction}
      onStatusChange={onStatusChange}
      isStatusUpdating={isStatusUpdating}
    />
  );
}
